import {useFlags} from 'launchdarkly-react-client-sdk';
import React from 'react';
import {matchPath, useLocation} from 'react-router-dom';
import {Button, Icon} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {ROUTES} from '@/config/routesConstants';
import Link, {LinkProps} from '@/components/link';
import useTrackButtonEvent from '@/hooks/analytics/use-track-button-event/useTrackButtonEvent';
import useEmberIframeStyleStore from '@/stores/emberIframeStyleStore';
import styles from './CampaignsViewSwitcher.module.scss';

const V1_TAB_ROW_HEIGHT = 50;

const BUTTON_NAMES = {
  'Switch to Classic View': 'switch_to_classic_view',
  'Switch to New View': 'switch_to_new_view',
} as const;

/**
 * The design system's Button forwards unknown props to MUI but its type isn't
 * generic over `component`, so react-router's `to` isn't assignable. Asserting
 * once here keeps every call site type-checked.
 */
const LinkButton = Button as React.ComponentType<
  React.ComponentProps<typeof Button> & Pick<LinkProps, 'to'>
>;

type Destination = {
  to: string;
  labelKey: keyof typeof BUTTON_NAMES;
  emphasized: boolean;
  needsStrip: boolean;
  screenName: 'campaigns_list' | 'campaign';
  campaignId?: string;
};

const LIST_DESTINATIONS: Record<string, Destination> = {
  [ROUTES.CAMPAIGNS_V2]: {
    to: ROUTES.CAMPAIGNS,
    labelKey: 'Switch to Classic View',
    emphasized: false,
    needsStrip: false,
    screenName: 'campaigns_list',
  },
  [ROUTES.CAMPAIGNS]: {
    to: ROUTES.CAMPAIGNS_V2,
    labelKey: 'Switch to New View',
    emphasized: true,
    needsStrip: true,
    screenName: 'campaigns_list',
  },
};

/**
 * V1 campaign tab -> nearest V2 tab, so switching keeps the user on the same
 * campaign instead of dropping them back on the list.
 */
const V1_TAB_TO_V2_TAB: Record<string, string> = {
  'brief': 'brief',
  'candidates': 'creators',
  'proposals': 'creators',
  'contracts': 'creators',
  'recruit': 'creators',
  'content-downloads': 'assets',
  'stats': 'statistics',
};

const DEFAULT_V2_TAB = 'creators';

function withoutTrailingSlash(pathname: string): string {
  return pathname.length > 1 && pathname.endsWith('/')
    ? pathname.slice(0, -1)
    : pathname;
}

function resolveDestination(pathname: string): Destination | undefined {
  const path = withoutTrailingSlash(pathname);

  const listDestination = LIST_DESTINATIONS[path];
  if (listDestination) {
    return listDestination;
  }

  const match = matchPath(`${ROUTES.CAMPAIGNS}/:campaignId/*`, path);
  const campaignId = match?.params.campaignId;
  if (!campaignId) {
    return undefined;
  }

  const [v1Tab] = (match.params['*'] ?? '').split('/');
  const v2Tab = V1_TAB_TO_V2_TAB[v1Tab] ?? DEFAULT_V2_TAB;

  return {
    to: `${ROUTES.CAMPAIGNS_V2}/${campaignId}/${v2Tab}`,
    labelKey: 'Switch to New View',
    emphasized: true,
    needsStrip: false,
    screenName: 'campaign',
    campaignId,
  };
}

function CampaignsViewSwitcher() {
  const {pathname} = useLocation();
  const {reactCampaignsV2} = useFlags();
  const {headerHeight} = useEmberIframeStyleStore();
  const {pressed} = useTrackButtonEvent();
  const destination = resolveDestination(pathname);

  if (!destination || !reactCampaignsV2) {
    return null;
  }

  const button = (
    <LinkButton
      appearance={destination.emphasized ? 'brand' : 'neutral'}
      mode="tinted"
      size="small"
      component={Link}
      to={destination.to}
      testID="campaigns-view-switcher"
      className={styles.button}
      icon={
        destination.emphasized ? (
          <Icon name="StateBar-Effects" size="small" appearance="brand" />
        ) : undefined
      }
      iconPosition="start"
      onClick={() =>
        pressed({
          button_name: BUTTON_NAMES[destination.labelKey],
          screen_name: destination.screenName,
          campaign_id: destination.campaignId ?? '',
        })
      }
    >
      {translate(destination.labelKey)}
    </LinkButton>
  );

  if (!destination.needsStrip) {
    return button;
  }

  // useSetEmberIframeStyles' additionalHeaderHeight can't be used to reserve
  // space here: MainRoutes calls the same hook with 0 and, as the parent, its
  // effect runs last and overwrites the shared CSS variables.
  return (
    <div
      className={styles.strip}
      style={{top: headerHeight, height: V1_TAB_ROW_HEIGHT}}
    >
      {button}
    </div>
  );
}

export default CampaignsViewSwitcher;
